import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import type { ScreenId } from '../../app/screenRoutes';
import { navigateToLandingSection } from '../../config/navigation';
import { carrierCabinetApi } from '../../features/carrierCabinet/api';
import { useFigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import { useAuth } from '../../state/auth';
import { useChatStatus } from '../../state/chatStatus';
import {
  CabinetSidebar,
  Footer,
  Header,
  MessageIcon,
  MobileDrawer,
  NotificationBell,
  ProfileIcon,
} from '../../ui';
import './carrier-cabinet-layout.css';

export type CarrierCabinetScreenId = Extract<ScreenId, `carrier-${string}`>;

const SIDEBAR_GROUPS: Array<{
  title: string;
  items: Array<{ screen: CarrierCabinetScreenId; label: string; icon: string }>;
}> = [
  {
    title: 'Кабинет',
    items: [
      { screen: 'carrier-dashboard', label: 'Главная', icon: 'carrier-sidebar-home.svg' },
      { screen: 'carrier-my-offers', label: 'Мои отклики', icon: 'carrier-sidebar-orders.svg' },
      { screen: 'carrier-chat-list', label: 'Сообщения', icon: 'carrier-sidebar-messages.svg' },
      { screen: 'carrier-notifications', label: 'Уведомления', icon: 'carrier-sidebar-bell.svg' },
      { screen: 'carrier-active-transport', label: 'Активные перевозки', icon: 'carrier-sidebar-car.svg' },
    ],
  },
  {
    title: 'Аккаунт',
    items: [
      { screen: 'carrier-profile-internal', label: 'Профиль', icon: 'carrier-sidebar-profile.svg' },
      { screen: 'carrier-documents', label: 'Документы', icon: 'carrier-sidebar-documents.svg' },
      { screen: 'carrier-settings', label: 'Настройки', icon: 'carrier-sidebar-settings.svg' },
      { screen: 'carrier-help', label: 'Помощь', icon: 'carrier-sidebar-help.svg' },
    ],
  },
];

function go(screen: string, params?: Record<string, string | number>) {
  const query = params ? `?${new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)])).toString()}` : '';
  window.location.hash = `${screen}${query}`;
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

interface CarrierCabinetLayoutProps {
  children: ReactNode;
  figmaNode: Partial<Record<'1920' | '1440' | '768' | '375', string>>;
  screen: CarrierCabinetScreenId;
}

export function CarrierCabinetLayout({ children, figmaNode, screen }: CarrierCabinetLayoutProps) {
  const breakpoint = useFigmaBreakpoint();
  const auth = useAuth();
  const chatStatus = useChatStatus();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileName, setProfileName] = useState(auth.user?.display_name || '');
  const activeFigmaNode = figmaNode[breakpoint] ?? figmaNode['1440'] ?? figmaNode['1920'];

  const userLabel = profileName || auth.user?.display_name || auth.user?.email.split('@')[0] || '';
  const sidebarActiveScreen = screen === 'carrier-search' || screen === 'carrier-request-detail' ? 'carrier-dashboard' : screen;
  const style = {
    '--carrier-workspace-gap': screen === 'carrier-dashboard' ? '36px' : '40px',
  } as CSSProperties;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [screen]);

  useEffect(() => {
    let active = true;
    if (auth.user?.display_name) {
      setProfileName(auth.user.display_name);
    }
    carrierCabinetApi.profile()
      .then((profile) => { if (active && profile.display_name) setProfileName(profile.display_name); })
      .catch(() => {});
    return () => { active = false; };
  }, [auth.user?.display_name]);

  return (
    <div
      className={`carrier-cabinet${breakpoint === '768' ? ' carrier-cabinet--tablet' : ''}`}
      data-screen={screen}
      data-figma-node={activeFigmaNode}
      data-figma-variant={breakpoint}
      style={style}
    >
      <Header
        authenticated
        actions={(
          <div className="carrier-cabinet__header-actions">
            <a
              href="#carrier-chat-list"
              aria-label="Сообщения"
              onClick={(e) => {
                e.preventDefault();
                go('carrier-chat-list');
              }}
            >
              <MessageIcon hasUnread={chatStatus.unreadMessages > 0} />
              {chatStatus.unreadMessages > 0 ? <span className="cabinet-header-count">{chatStatus.unreadMessages > 99 ? '99+' : chatStatus.unreadMessages}</span> : null}
            </a>
            <a
              href="#carrier-notifications"
              aria-label="Уведомления"
              onClick={(e) => {
                e.preventDefault();
                go('carrier-notifications');
              }}
            >
              <NotificationBell hasUnread={chatStatus.unreadNotifications > 0} />
              {chatStatus.unreadNotifications > 0 ? <span className="cabinet-header-count">{chatStatus.unreadNotifications > 99 ? '99+' : chatStatus.unreadNotifications}</span> : null}
            </a>
          </div>
        )}
        account={(
          <a
            className="lovo-header__account-btn"
            href="#carrier-profile-internal"
            onClick={(e) => {
              e.preventDefault();
              go('carrier-profile-internal');
            }}
          >
            <span>{userLabel}</span>
            <img src="/figma/assets/profile-arrow-up-right.svg" alt="" aria-hidden="true" />
          </a>
        )}
        onLogoClick={() => go('landing')}
        menuExpanded={menuOpen}
        externalDrawer
        onMenuToggle={() => setMenuOpen((value) => !value)}
      />

      <div className="carrier-cabinet__workspace">
        <CabinetSidebar
          groups={SIDEBAR_GROUPS}
          activeScreen={sidebarActiveScreen}
          onNavigate={(targetScreen) => {
            go(targetScreen);
          }}
          unreadMessages={chatStatus.unreadMessages}
          unreadNotifications={chatStatus.unreadNotifications}
        />

        <main className="carrier-cabinet__main">{children}</main>
      </div>

      {menuOpen ? (
        <MobileDrawer
          variant="carrier"
          activeAction={sidebarActiveScreen as any}
          onClose={() => setMenuOpen(false)}
          onLogoClick={() => {
            setMenuOpen(false);
            go('landing');
          }}
          onNavigate={(action) => {
            setMenuOpen(false);
            if (action === 'how-it-works') {
              navigateToLandingSection('how');
              return;
            }
            if (action === 'why-lovo' || action === 'reviews') {
              navigateToLandingSection('reviews');
              return;
            }
            if (action === 'logo' || action === 'help') {
              go('landing');
              return;
            }
            if (action === 'carrier-dashboard') go('carrier-dashboard');
            if (action === 'carrier-my-offers') go('carrier-my-offers');
            if (action === 'carrier-chat-list') go('carrier-chat-list');
            if (action === 'carrier-notifications') go('carrier-notifications');
            if (action === 'carrier-active-transport') go('carrier-active-transport');
            if (action === 'carrier-profile-internal') go('carrier-profile-internal');
            if (action === 'carrier-documents') go('carrier-documents');
            if (action === 'carrier-help') go('carrier-help');
          }}
        />
      ) : null}

      <Footer
        size={breakpoint}
        legalDetails={{
          legalName: 'ООО/ИП “ХХХ”',
          inn: '123456789012',
          ogrnip: '12345678901234567',
          year: 2026,
        }}
        onNavigate={(action) => {
          if (action === 'logo') go('landing');
          if (action === 'customer-cabinet') go('my-requests');
          if (action === 'carrier-cabinet') go('carrier-dashboard');
          if (action === 'carrier-registration') go('carrier-rega-email');
        }}
      />
    </div>
  );
}

export function navigateCarrier(screen: CarrierCabinetScreenId, params?: Record<string, string | number>) {
  go(screen, params);
}
